import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { usandoBancoExterno } from "@/lib/banco-modo";
import { logAuthCliente } from "@/lib/auth-log";
import { obterClienteDados } from "@/lib/obter-dados";
import { obterUsuarioCliente } from "@/lib/cliente-dados-helper";
import devConfig from "@/../config/dev.config.json";

export type Papel = "admin" | "gerente" | "operador" | "estagiario" | "suspenso" | "ferias";

export const NIVEIS: { papel: Papel; nivel: number; rotulo: string }[] = [
  { papel: "admin", nivel: 1, rotulo: "Administrador" },
  { papel: "gerente", nivel: 2, rotulo: "Gerente" },
  { papel: "operador", nivel: 3, rotulo: "Operador" },
  { papel: "estagiario", nivel: 4, rotulo: "Estagiário" },
  { papel: "suspenso", nivel: 5, rotulo: "Suspenso" },
  { papel: "ferias", nivel: 6, rotulo: "Férias" },
];

export function rotuloPapel(papel?: string | null) {
  return NIVEIS.find((n) => n.papel === papel)?.rotulo ?? "Sem perfil";
}

function nivelDoPapel(papel: unknown) {
  return NIVEIS.find((n) => n.papel === papel)?.nivel ?? 9;
}

function nivelSeguro(valor: unknown) {
  const nivel = Math.trunc(Number(valor ?? 9));
  return nivel >= 1 && nivel <= 6 ? nivel : 9;
}

/** 1 = administrador, 2 = gerente, 3 = operador, 4 = estagiário, 5 = suspenso, 6 = férias, 9 = sem perfil */
export function useNivel() {
  const { user, loading } = useAuth();
  const q = useQuery({
    queryKey: [
      "role_level",
      user?.id,
      usandoBancoExterno() ? "externo" : "cloud",
      usandoBancoExterno() ? user?.user_metadata?.["nivel"] : null,
    ],
    enabled: !!user?.id,
    refetchInterval: devConfig?.suspenderFnFunctionsPeriodicas ? false : 30000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const id = user?.id;
      if (!id) return 9;
      const banco = await obterClienteDados();
      if (usandoBancoExterno()) {
        if (devConfig?.logarSSE === true) {
          logAuthCliente("useNivel: consultando usuário externo revalidado", {
            usuarioId: user?.id,
          });
        }
        const usuario = await obterUsuarioCliente();
        const bruto = usuario?.user_metadata?.["nivel"];
        const revalidado = nivelSeguro(bruto);
        const nivelDaSessao = nivelSeguro(user?.user_metadata?.["nivel"]);
        const normalizado = revalidado === 9 ? nivelDaSessao : revalidado;
        if (devConfig?.logarSSE === true) {
          logAuthCliente("useNivel: nível externo resolvido", {
            bruto,
            revalidado,
            nivelDaSessao,
            normalizado,
          });
        }
        return normalizado;
      }
      const { data, error } = await (banco as any).rpc("role_level", { _user_id: id });
      if (!error) {
        const normalizado = nivelSeguro(data);
        logAuthCliente("useNivel: nível da nuvem resolvido por role_level", {
          bruto: data,
          normalizado,
        });
        return normalizado;
      }
      logAuthCliente("useNivel: role_level falhou; tentando user_roles", error);

      const { data: papel, error: erroPapel } = await (banco as any)
        .from("user_roles")
        .select("role")
        .eq("user_id", id)
        .limit(1)
        .maybeSingle();
      if (erroPapel) throw erroPapel;
      return nivelDoPapel(papel?.role);
    },
  });
  const nivelSessao = nivelSeguro(user?.user_metadata?.["nivel"]);
  // No banco externo, o nível vem junto da sessão criada no login. Ele deve
  // prevalecer sobre um valor 9 eventualmente mantido no cache após uma
  // revalidação temporariamente indisponível no servidor IIS.
  const nivel = usandoBancoExterno() && nivelSessao !== 9 ? nivelSessao : (q.data ?? nivelSessao);
  /*
  logAuthCliente("useNivel: resultado entregue à interface", {
    usuarioId: user?.id ?? null,
    modo: usandoBancoExterno() ? "externo" : "cloud",
    nivelSessao,
    nivelConsulta: q.data ?? null,
    nivelFinal: nivel,
    consultaCarregando: q.isLoading,
    consultaErro: q.error instanceof Error ? q.error.message : null,
  });
  */
  return {
    nivel,
    carregando: loading || (!!user?.id && q.isLoading && nivelSessao === 9),
    ehAdmin: nivel === 1,
    ehEstagiario: nivel === 4,
    ehSuspenso: nivel === 5,
    rabelo: user?.user_metadata?.["rabelo"] === true,
    arquivo: user?.user_metadata?.["arquivo"] === true,
    regressivas: user?.user_metadata?.["regressivas"] === true,
  };
}

export function Permissao({ nivelMaximo, children }: { nivelMaximo: number; children: ReactNode }) {
  const { nivel, carregando } = useNivel();
  if (carregando) return <p className="text-sm text-muted-foreground">Carregando…</p>;
  if (nivel > nivelMaximo)
    return (
      <div className="card-soft p-6">
        <h1 className="text-lg font-semibold">Acesso restrito</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Seu perfil não tem permissão para acessar esta área. Fale com um administrador.
        </p>
      </div>
    );
  return <>{children}</>;
}
